import React from 'react'
import { ContextRoute } from '../app/context'
import Router from '../router'

interface Props {
    traceId: string
    router: Router
    route: ContextRoute
    aiFeatureFlagEnabled: boolean
    environment: string
    service: string
}

interface TraceRouteParameters {
    organization: string
    application: string
    profile: string
}

interface State {
    copiedLabel: string | null
}

export class TraceCopyDropdown extends React.Component<Props, State> {
    public state: State = {
        copiedLabel: null,
    }

    public render() {
        if (!this.clipboardApiAvailable()) {
            return null
        }

        const copyLabel = this.state.copiedLabel ? 'Copied' : 'Copy'

        return (
            <div className="btn-group pull-right" style={{ marginLeft: '1em' }}>
                <button type="button" className="btn btn-default dropdown-toggle" data-toggle="dropdown">
                    <i className="fa fa-copy" /> {copyLabel} <span className="caret" />
                </button>
                <ul className="dropdown-menu dropdown-menu-right" role="menu">
                    {this.renderCopyMenuItem('Trace URL', 'fa-link', this.copyTraceUrl)}
                    {this.renderCopyMenuItem('Trace ID', 'fa-hashtag', this.copyTraceId)}
                    {this.props.aiFeatureFlagEnabled ? (
                        <React.Fragment>
                            {this.renderCopyMenuItem('Tideways CLI command', 'fa-terminal', this.copyTidewaysCommand)}
                            {this.renderCopyMenuItem('Markdown for AI advice', 'fa-file-text-o', this.copyMarkdown)}
                        </React.Fragment>
                    ) : (
                        this.renderBetaFeaturesMenuItem()
                    )}
                </ul>
            </div>
        )
    }

    private renderCopyMenuItem(label: string, icon: string, onClick: () => void) {
        return (
            <li>
                <a
                    href="#"
                    onClick={event => {
                        event.preventDefault()
                        onClick()
                    }}
                >
                    <i className={`fa ${icon}`} /> {label}
                </a>
            </li>
        )
    }

    private renderBetaFeaturesMenuItem() {
        return (
            <li>
                <a
                    href={this.betaFeaturesUrl()}
                    style={{
                        backgroundColor: '#fcf8e3',
                        color: '#8a6d3b',
                        fontWeight: 600,
                    }}
                >
                    <i className="fa fa-flask" /> Enable AI Agents Support for more options
                </a>
            </li>
        )
    }

    private copyTraceUrl = () => {
        this.copyToClipboard('Trace URL', this.absoluteTraceUrl())
    }

    private copyTraceId = () => {
        this.copyToClipboard('Trace ID', this.props.traceId)
    }

    private copyTidewaysCommand = () => {
        this.copyToClipboard('Tideways CLI command', `tideways trace show -- '${this.props.traceId}'`)
    }

    private copyMarkdown = () => {
        fetch(this.markdownUrl(), { credentials: 'same-origin' })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Could not load trace markdown.')
                }

                return response.text()
            })
            .then(markdown => this.copyToClipboard('Markdown', markdown))
            .catch(() => this.setState({ copiedLabel: null }))
    }

    private absoluteTraceUrl = (): string => {
        return window.location.origin + this.props.router.path('XhprofProfilerBundle.Profile.quick', this.traceRouteParameters())
    }

    private markdownUrl = (): string => {
        return this.props.router.path('XhprofProfilerBundle.Profile.markdown', this.traceRouteParameters())
    }

    private betaFeaturesUrl = (): string => {
        const routeParameters = this.traceRouteParameters()

        return this.props.router.path('XhprofOrganizationBundle.BetaFeatures.list', {
            organization: routeParameters.organization,
            project: routeParameters.application,
            trace_id: routeParameters.profile,
            environment: this.props.environment,
            service: this.props.service,
        })
    }

    private traceRouteParameters = (): TraceRouteParameters => {
        const routeParameters = this.props.route.urlParameters || this.props.route.parameters
        const organization = routeParameters.organization
        const application = routeParameters.application

        if (!organization || !application) {
            throw new Error('Trace copy links require organization and application route parameters.')
        }

        return {
            organization,
            application,
            profile: this.props.traceId,
        }
    }

    private copyToClipboard = (label: string, value: string) => {
        navigator.clipboard.writeText(value).then(() => this.setState({ copiedLabel: label }))
    }

    private clipboardApiAvailable = () => {
        return navigator.clipboard && typeof navigator.clipboard.writeText !== 'undefined'
    }
}
